import { cn } from '../lib/cn';

/**
 * `13 §4`: **mandatory for every piece of LLM-generated text.**
 *
 * The threat is prompt injection reaching the manager's browser (`14 §4`): an
 * employee's output is attacker-influenceable content, and rendering it as HTML
 * would make a task description an XSS vector.
 *
 * So this deliberately does not render HTML at all. It renders a small, closed
 * set of Markdown — paragraphs, line breaks, headings, inline code, bold and
 * italics, fenced code, blockquotes, and bullet and numbered lists — as React
 * elements. There is no `dangerouslySetInnerHTML` anywhere in it, which is the
 * property worth having: a bug here produces ugly text, not script execution.
 * Links are shown as text rather than anchors for the same reason — a
 * `javascript:` or credential-stealing URL the manager can read is safer than
 * one they can click.
 *
 * Single newlines inside a paragraph become line breaks, the way a chat
 * comment reads: the model's line breaks are intentional, and joining them
 * with spaces is how a task description turned into one solid wall of text.
 */
export function SafeMarkdown({ children, className }: { children: string; className?: string }) {
  return <div className={cn('space-y-3 text-sm text-ink', className)}>{blocksOf(children)}</div>;
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*[-*]\s+/;
const NUMBERED = /^\s*\d{1,3}[.)]\s+/;
const QUOTED = /^>\s?/;

function blocksOf(source: string) {
  const blocks: React.ReactNode[] = [];
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  // `noUncheckedIndexedAccess` is on: every read is widened to `| undefined`,
  // and a past-the-end line is the empty string as far as this parser cares.
  const at = (i: number): string => lines[i] ?? '';

  let index = 0;
  let key = 0;

  while (index < lines.length) {
    const line = at(index);

    if (line.trim() === '') {
      index += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !at(index).startsWith('```')) {
        body.push(at(index));
        index += 1;
      }
      index += 1;
      blocks.push(
        <pre key={key++} className="overflow-x-auto rounded bg-raised p-3 font-mono text-xs">
          <code>{body.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    const heading = HEADING.exec(line);

    if (heading !== null) {
      // Clamped to h3–h6: the page owns h1 and h2, and an employee's text must
      // not outrank the screen it is embedded in.
      const level = Math.min((heading[1] ?? '#').length + 2, 6);
      const Tag = `h${level}` as 'h3' | 'h4' | 'h5' | 'h6';
      blocks.push(
        <Tag key={key++} className={cn('font-semibold text-ink', level <= 4 ? 'text-base' : 'text-sm')}>
          {inline(heading[2] ?? '')}
        </Tag>,
      );
      index += 1;
      continue;
    }

    if (QUOTED.test(line)) {
      const quoted: string[] = [];
      while (index < lines.length && QUOTED.test(at(index))) {
        quoted.push(at(index).replace(QUOTED, ''));
        index += 1;
      }
      blocks.push(
        <blockquote key={key++} className="border-l-2 border-line pl-3 text-ink-soft">
          {withBreaks(quoted)}
        </blockquote>,
      );
      continue;
    }

    if (BULLET.test(line)) {
      const items: string[] = [];
      while (index < lines.length && BULLET.test(at(index))) {
        items.push(at(index).replace(BULLET, ''));
        index += 1;
      }
      blocks.push(
        <ul key={key++} className="list-disc space-y-1 pl-5">
          {items.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (NUMBERED.test(line)) {
      const items: string[] = [];
      while (index < lines.length && NUMBERED.test(at(index))) {
        items.push(at(index).replace(NUMBERED, ''));
        index += 1;
      }
      blocks.push(
        <ol key={key++} className="list-decimal space-y-1 pl-5">
          {items.map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (
      index < lines.length &&
      at(index).trim() !== '' &&
      !at(index).startsWith('```') &&
      !HEADING.test(at(index)) &&
      !QUOTED.test(at(index)) &&
      !BULLET.test(at(index)) &&
      !NUMBERED.test(at(index))
    ) {
      paragraph.push(at(index));
      index += 1;
    }
    blocks.push(<p key={key++}>{withBreaks(paragraph)}</p>);
  }

  return blocks;
}

/** Each source line on its own rendered line, the way the author broke them. */
function withBreaks(lines: string[]) {
  return lines.map((line, i) => (
    <span key={i}>
      {i > 0 ? <br /> : null}
      {inline(line)}
    </span>
  ));
}

/**
 * Code spans first, so markers inside backticks stay literal; bold and italics
 * on the rest. Every other construct — links above all — is left as literal
 * text on purpose. The emphasis patterns demand a closing marker and content
 * without the marker in it, so an unclosed `**` or a glob like `*.ts` falls
 * through as plain text instead of backtracking.
 */
function inline(text: string) {
  return text.split(/(`[^`]+`)/g).map((part, i) =>
    part.startsWith('`') && part.endsWith('`') && part.length > 1 ? (
      <code key={i} className="rounded bg-raised px-1 py-0.5 font-mono text-xs">
        {part.slice(1, -1)}
      </code>
    ) : (
      <span key={i}>{emphasis(part)}</span>
    ),
  );
}

function emphasis(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') && part.length > 4 ? (
      <strong key={i}>{italics(part.slice(2, -2))}</strong>
    ) : (
      <span key={i}>{italics(part)}</span>
    ),
  );
}

function italics(text: string) {
  return text.split(/(\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g).map((part, i) => {
    const marked =
      part.length > 2 &&
      ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_')));

    return marked ? <em key={i}>{part.slice(1, -1)}</em> : <span key={i}>{part}</span>;
  });
}
